import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, ChevronLeft, Calendar, FileEdit, ChevronDown, ChevronRight, Users, X } from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { useRef } from 'react';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface Person {
  id: string;
  name: string;
  email: string | null;
}

interface MeetingSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  template_content: string | null;
  default_attendee_ids?: string[];
}

interface MeetingInstance {
  id: string;
  series_id: string;
  title: string | null;
  meeting_date: string;
  summary: string | null;
  created_at: string;
}

export function MeetingSeriesPage() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const toast = useToast();
  const [series, setSeries] = useState<MeetingSeries | null>(null);
  const [instances, setInstances] = useState<MeetingInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateLastSaved, setTemplateLastSaved] = useState<string | null>(null);
  const [defaultAttendeesOpen, setDefaultAttendeesOpen] = useState(false);
  const [allPeople, setAllPeople] = useState<Person[]>([]);
  const [showAddDefault, setShowAddDefault] = useState(false);
  const [defaultSearch, setDefaultSearch] = useState('');
  const addDefaultRef = useRef<HTMLDivElement>(null);
  const defaultInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiGet<Person[]>('/people').then(setAllPeople).catch(console.error);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addDefaultRef.current && !addDefaultRef.current.contains(e.target as Node)) {
        setShowAddDefault(false);
        setDefaultSearch('');
      }
    }
    if (showAddDefault) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showAddDefault]);

  useEffect(() => {
    if (showAddDefault && defaultInputRef.current) {
      defaultInputRef.current.focus();
    }
  }, [showAddDefault]);

  async function addDefaultAttendee(personId: string) {
    if (!series) return;
    const oldIds = series.default_attendee_ids ?? [];
    const newIds = [...oldIds, personId];
    setSeries({ ...series, default_attendee_ids: newIds });
    setShowAddDefault(false);
    setDefaultSearch('');
    try {
      const updated = await apiPatch<MeetingSeries & { default_attendee_ids: string[] }>(
        `/meeting-series/${seriesId}`, { default_attendee_ids: newIds }
      );
      setSeries(prev => prev ? { ...prev, default_attendee_ids: updated.default_attendee_ids ?? newIds } : prev);
    } catch (err) {
      console.error('Failed to update default attendees:', err);
      setSeries(prev => prev ? { ...prev, default_attendee_ids: oldIds } : prev);
      toast.error('Failed to update default attendees');
    }
  }

  async function removeDefaultAttendee(personId: string) {
    if (!series) return;
    const oldIds = series.default_attendee_ids ?? [];
    const newIds = oldIds.filter(id => id !== personId);
    setSeries({ ...series, default_attendee_ids: newIds });
    try {
      const updated = await apiPatch<MeetingSeries & { default_attendee_ids: string[] }>(
        `/meeting-series/${seriesId}`, { default_attendee_ids: newIds }
      );
      setSeries(prev => prev ? { ...prev, default_attendee_ids: updated.default_attendee_ids ?? newIds } : prev);
    } catch (err) {
      console.error('Failed to update default attendees:', err);
      setSeries(prev => prev ? { ...prev, default_attendee_ids: oldIds } : prev);
      toast.error('Failed to update default attendees');
    }
  }

  async function load() {
    if (!seriesId) return;
    try {
      const [s, i] = await Promise.all([
        apiGet<MeetingSeries>(`/meeting-series/${seriesId}`),
        apiGet<MeetingInstance[]>(`/meeting-series/${seriesId}/instances`),
      ]);
      setSeries({ ...s, default_attendee_ids: s.default_attendee_ids ?? [] });
      setInstances(i);
    } catch (err) {
      console.error('Failed to load meeting series:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [seriesId]);

  const handleTemplateUpdate = useCallback(async (content: JSONContent) => {
    if (!seriesId) return;
    setTemplateSaving(true);
    try {
      await apiPatch(`/meeting-series/${seriesId}`, {
        template_content: JSON.stringify(content),
      });
      setTemplateLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  }, [seriesId]);

  async function handleCreateInstance(date: string, title: string) {
    if (!seriesId) return;
    try {
      await apiPost(`/meeting-series/${seriesId}/instances`, {
        meeting_date: date,
        title: title || null,
      });
      setShowNewModal(false);
      toast.success('Meeting created');
      load();
    } catch (err) {
      console.error('Failed to create meeting:', err);
      toast.error('Failed to create meeting');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!series) {
    return <div className="h-full overflow-auto p-6"><div className="text-text-muted text-center py-16">Meeting series not found.</div></div>;
  }

  return (
    <div className="h-full overflow-auto p-6">
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/meetings" className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-3">
          <ChevronLeft size={16} /> Back to Meetings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">{series.name}</h1>
            {series.description && (
              <p className="text-sm text-text-muted mt-1">{series.description}</p>
            )}
            {series.cadence && (
              <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-bg-input rounded-full text-text-secondary capitalize">
                {series.cadence}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            <Plus size={16} />
            New Meeting
          </button>
        </div>
      </div>

      {/* Template Editor */}
      <div className="mb-6 border border-border rounded-lg overflow-hidden">
        <button
          onClick={() => setTemplateOpen(!templateOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {templateOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <FileEdit size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Meeting Template</span>
          {templateSaving && <span className="text-xs text-accent">Saving...</span>}
          {templateLastSaved && !templateSaving && (
            <span className="text-xs text-text-muted">Saved at {templateLastSaved}</span>
          )}
        </button>
        {templateOpen && (
          <div className="p-4 border-t border-border">
            <p className="text-xs text-text-muted mb-3">
              This template is used as the starting content for new meetings in this series.
            </p>
            <div className="template-editor">
              <DossierEditor
                content={series.template_content ? (() => { try { return JSON.parse(series.template_content); } catch { return null; } })() : null}
                onUpdate={handleTemplateUpdate}
                editable={true}
                placeholder="Set up a template for new meetings (e.g., agenda sections, standard topics)..."
              />
            </div>
          </div>
        )}
      </div>

      {/* Default Attendees */}
      <div className="mb-6 border border-border rounded-lg">
        <button
          onClick={() => setDefaultAttendeesOpen(!defaultAttendeesOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-bg-secondary hover:bg-bg-hover transition-colors"
        >
          {defaultAttendeesOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Users size={14} className="text-text-muted" />
          <span className="text-sm font-medium text-text-primary flex-1">Default Attendees</span>
          {(series.default_attendee_ids ?? []).length > 0 && (
            <span className="text-xs text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
              {(series.default_attendee_ids ?? []).length}
            </span>
          )}
        </button>
        {defaultAttendeesOpen && (
          <div className="p-4 border-t border-border">
            <p className="text-xs text-text-muted mb-3">
              People who normally attend. They'll be pre-checked on new meetings.
            </p>
            <div className="space-y-1.5">
              {(series.default_attendee_ids ?? []).map(id => {
                const person = allPeople.find(p => p.id === id);
                if (!person) return null;
                return (
                  <div key={id} className="flex items-center gap-2 group py-1 px-1 rounded hover:bg-bg-hover transition-colors">
                    <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      {getInitials(person.name)}
                    </div>
                    <span className="text-sm text-text-primary truncate flex-1">{person.name}</span>
                    <button
                      onClick={() => removeDefaultAttendee(person.id)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-text-muted hover:text-danger transition-all flex-shrink-0"
                      title="Remove default attendee"
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}

              <div className="relative" ref={addDefaultRef}>
                {showAddDefault ? (
                  <div>
                    <input
                      ref={defaultInputRef}
                      type="text"
                      value={defaultSearch}
                      onChange={e => setDefaultSearch(e.target.value)}
                      placeholder="Search people..."
                      className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                      onKeyDown={e => {
                        if (e.key === 'Escape') {
                          setShowAddDefault(false);
                          setDefaultSearch('');
                        }
                      }}
                    />
                    {(() => {
                      const available = allPeople.filter(
                        p => !(series.default_attendee_ids ?? []).includes(p.id) &&
                          (defaultSearch === '' || p.name.toLowerCase().includes(defaultSearch.toLowerCase()))
                      );
                      return (
                        <>
                          {available.length > 0 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-36 overflow-y-auto bg-bg-surface border border-border rounded-lg shadow-lg">
                              {available.map(person => (
                                <button
                                  key={person.id}
                                  onClick={() => addDefaultAttendee(person.id)}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-bg-hover transition-colors"
                                >
                                  <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold flex-shrink-0">
                                    {getInitials(person.name)}
                                  </div>
                                  <span className="text-xs text-text-primary truncate">{person.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {available.length === 0 && defaultSearch && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-bg-surface border border-border rounded-lg shadow-lg px-3 py-2">
                              <span className="text-xs text-text-muted">No matching people</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddDefault(true)}
                    className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors py-1"
                  >
                    <Plus size={12} />
                    Add attendee
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {instances.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <Calendar size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No meetings yet</p>
          <p className="text-sm">Create a new meeting instance to start taking notes.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {instances.map((inst) => (
            <Link
              key={inst.id}
              to={`/meetings/${series.id}/${inst.id}`}
              className="block bg-bg-card border border-border rounded-lg p-4 hover:border-border-hover transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-text-primary group-hover:text-accent transition-colors">
                    {inst.title || formatDate(inst.meeting_date)}
                  </h3>
                  {inst.title && (
                    <p className="text-xs text-text-muted mt-0.5">{formatDate(inst.meeting_date)}</p>
                  )}
                  {inst.summary && (
                    <p className="text-xs text-text-muted mt-1 truncate max-w-lg">{inst.summary}</p>
                  )}
                </div>
                <span className="text-xs text-text-muted">{formatRelativeDate(inst.meeting_date)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showNewModal && (
        <NewInstanceModal
          seriesName={series.name}
          onSave={handleCreateInstance}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </div>
    </div>
  );
}

function NewInstanceModal({
  seriesName,
  onSave,
  onClose,
}: {
  seriesName: string;
  onSave: (date: string, title: string) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [title, setTitle] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-1">New Meeting</h2>
        <p className="text-sm text-text-muted mb-4">{seriesName}</p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Date *</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`e.g., ${seriesName} — ${formatDate(date)}`}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors">
            Cancel
          </button>
          <button
            onClick={() => date && onSave(date, title)}
            disabled={!date}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  if (diff < 0) {
    const absDiff = Math.abs(diff);
    if (absDiff < 7) return `In ${absDiff} days`;
    if (absDiff < 30) return `In ${Math.floor(absDiff / 7)} weeks`;
    return formatDate(dateStr);
  }
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  return formatDate(dateStr);
}
